import { styled } from '@mui/material/styles';

import { DndContext, DragEndEvent, closestCenter } from '@dnd-kit/core';
import { SortableContext, arrayMove, useSortable } from '@dnd-kit/sortable';

import { CSS } from '@dnd-kit/utilities';
import { CSSProperties, type JSX } from 'react';
import { KPI } from '../../../../components/Card/KPI';
import { Card } from '../../../../components/Card/Card';
import { GenericFallbacksWrapper } from '../../../../components/Fallback/GenericFallbacksWrapper';

const List = styled('ul')`
  list-style-type: none;
  margin: 0;
  padding: 0;
  gap: 0.4rem;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
`;

const ListItem = styled('li')`
  margin: 0;
  padding: 0;
`;

export interface ICardProps {
  id: string;
  title: string;
  value: string;
  indicator: number | null;
  description?: string;
}

interface ICardListProps {
  kpis: ICardProps[];
  onDelete: (cardId: string) => void;
  onReorder: (itemIds: string[]) => void;
  style?: CSSProperties;
  disableDrag?: boolean;
}

interface SortableItemProps {
  kpi: ICardProps;
  onDelete: (cardId: string) => void;
  disableDrag?: boolean;
}

const SortableItem = ({ kpi, onDelete, disableDrag }: SortableItemProps): JSX.Element => {
  const { setNodeRef, transform, transition, listeners, attributes } = useSortable({
    id: kpi.id,
    disabled: disableDrag,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };
  return (
    <GenericFallbacksWrapper style={{ maxWidth: '500px', maxHeight: '200px' }}>
      <ListItem ref={setNodeRef} style={style} key={kpi.id} id={String(kpi.id)}>
        <Card>
          <KPI
            title={kpi.title}
            value={kpi.value}
            attributes={disableDrag ? undefined : attributes}
            listeners={disableDrag ? {} : listeners}
            indicator={kpi.indicator}
            onDelete={disableDrag ? undefined : () => onDelete(kpi.id)}
            description={kpi.description}
          />
        </Card>
      </ListItem>
    </GenericFallbacksWrapper>
  );
};

export const CardList = ({ kpis, onReorder, onDelete, style, disableDrag = false }: ICardListProps) => {
  const handleReorder = (e: DragEndEvent) => {
    if (disableDrag) {
      return;
    }

    const { active, over } = e;
    if (active.id !== over?.id) {
      const kpiIds = kpis.map((k) => k.id);
      const oldIndex = kpiIds.indexOf(String(active.id));
      const newIndex = kpiIds.indexOf(over?.id ? String(over?.id) : '');
      const reOrderedKpiIds = arrayMove(kpiIds, oldIndex, newIndex);
      onReorder(reOrderedKpiIds);
    }
  };

  return (
    <List style={style}>
      <DndContext collisionDetection={closestCenter} onDragEnd={handleReorder}>
        <SortableContext items={kpis}>
          {kpis.map((kpi) => (
            <SortableItem key={kpi.id} kpi={kpi} onDelete={onDelete} disableDrag={disableDrag} />
          ))}
        </SortableContext>
      </DndContext>
    </List>
  );
};
